import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import axios        from 'axios'
import Landing     from './pages/Landing'
import Login       from './pages/Login'
import Dashboard   from './pages/Dashboard'
import PanelAdmin  from './pages/PanelAdmin'
import PanelProfesor from './pages/PanelProfesor'
import Precios     from './pages/Precios'
import Checkout    from './pages/Checkout'
import ExamenNivel from './pages/ExamenNivel'
import PlanVencido from './pages/PlanVencido'
import EmailConfirmado from './pages/EmailConfirmado'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)
const API = process.env.REACT_APP_API_URL

export default function App() {
  const [pantalla,      setPantalla]      = useState('landing')
  const [usuario,       setUsuario]       = useState(null)
  const [rolUsuario,    setRolUsuario]    = useState(null) // 'estudiante' | 'profesor' | 'admin' — elegido en Login.jsx
  const [planElegido,   setPlanElegido]   = useState(null)
  const [nivelExamen,   setNivelExamen]   = useState(null)
  const [datosRevision, setDatosRevision] = useState(null) // escritura + grabaciones para el profesor
  // true cuando el estudiante entro por el boton "Get Started" del landing
  // (registro directo, sin pasar primero por el examen ni por elegir plan)
  const [entrarSoloEstudiante, setEntrarSoloEstudiante] = useState(false)

  // El link del correo de confirmacion de Supabase redirige aqui, pero trae
  // los datos de la confirmacion en la URL (parametros o en el hash, segun
  // el tipo de link que genera Supabase) -- no basta con mirar la ruta. Si
  // el link trae un "token_hash", hay que confirmarlo explicitamente con
  // supabase.auth.verifyOtp(): Supabase no activa la sesion solo por visitar
  // la pagina. Sin este paso, nunca queda una sesion activa y el boton
  // "Elegir mi plan" de la pantalla de bienvenida no tiene con que seguir
  // (por eso mandaba de vuelta al landing en vez de a precios).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const hash   = new URLSearchParams(window.location.hash.replace('#', '?'))

    const esConfirmacion = params.get('type') === 'signup' ||
                           hash.get('type')   === 'signup'  ||
                           params.get('token_hash') !== null ||
                           window.location.pathname === '/email-confirmado'

    if (!esConfirmacion) return

    const tokenHash = params.get('token_hash')
    ;(tokenHash
      ? supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'signup' })
      : Promise.resolve()
    ).catch(() => {}).finally(() => {
      setPantalla('email-confirmado')
    })
  }, [])

  // Boton "Elegir mi plan" en la pantalla de bienvenida (EmailConfirmado.jsx):
  // en vez de recargar toda la pagina (lo que obligaria a detectar la sesion
  // desde cero, y perderla si algo falla), usamos la sesion que verifyOtp ya
  // dejo activa en este mismo cargue de pagina y seguimos el mismo camino
  // que un login normal -- entrarSegunEstadoPlan() lo manda a precios porque
  // todavia no tiene plan.
  async function handleContinuarConfirmado() {
    const { data } = await supabase.auth.getSession()
    const user = data?.session?.user
    if (!user) {
      // El link de confirmacion ya se uso o vencio -- que inicie sesion
      // manualmente con su correo y contraseña.
      setEntrarSoloEstudiante(true)
      setPantalla('login')
      return
    }
    setUsuario(user)
    setRolUsuario('estudiante')
    await entrarSegunEstadoPlan(user, 'estudiante')
  }

  function handleCrearCuenta() {
    setPlanElegido(null)
    setEntrarSoloEstudiante(true)
    setPantalla('login')
  }

  // Si el estudiante vuelve de pagar con un metodo que necesita salir del
  // sitio (PSE, por ejemplo), Wompi lo redirige aqui con ?pago=<referencia>.
  // Como esto recarga la pagina por completo, recuperamos su sesion de
  // Supabase (que persiste en el navegador) para no perder el login, y lo
  // mandamos derecho a la pantalla que confirma el pago.
  const [idTransaccionRedirigida, setIdTransaccionRedirigida] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (!params.get('pago')) return
    setIdTransaccionRedirigida(params.get('id') || null)
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.user) {
        setUsuario(data.session.user)
        setRolUsuario('estudiante')
        setPantalla('checkout-confirmando')
      }
      window.history.replaceState({}, '', window.location.pathname)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleLogin(user, rol) {
    setUsuario(user)
    setRolUsuario(rol)
    // Crea (si no existe) la fila del usuario en la base de datos de
    // EnglishBridge. Es seguro llamarlo en cada login: el backend solo
    // inserta si todavia no existe una fila para este usuario_id.
    // IMPORTANTE: hay que esperar (await) a que esto termine antes de
    // seguir — si el usuario va derecho a Checkout despues de registrarse,
    // la tabla "pagos" tiene una relacion hacia "usuarios" y el pago
    // fallaria si se intenta crear antes de que esta fila exista.
    if (user?.id) {
      try {
        await axios.post(`${API}/progreso/inicializar-usuario`, {
          usuario_id: user.id,
          email: user.email
        })
      } catch (err) {
        console.error('Error inicializando usuario:', err)
      }
    }

    // Si eligio un plan pago nuevo (desde Precios), siempre va primero a
    // pagarlo, sin importar el estado de un plan anterior.
    if (planElegido && !planElegido.esFree && planElegido.precio > 0) {
      setPantalla('checkout')
      return
    }

    // El plan Free se activa aqui. Esto cubre dos caminos: 1) el de
    // siempre, cuando se elige el plan ANTES de registrarse (Login.jsx ya
    // lo activa tambien ahi, pero llamarlo de nuevo aqui no hace daño -- el
    // backend no deja pisar un plan que ya existe, ver pagos.js); y 2) el
    // boton "Get Started" del landing, donde la cuenta se crea PRIMERO y el
    // plan se elige DESPUES de confirmar el correo -- ahi este es el UNICO
    // lugar donde se activa, porque planElegido no sobrevive el refresco
    // de pagina que pasa al confirmar el correo.
    if (planElegido?.esFree && rol === 'estudiante') {
      try {
        await axios.post(API + '/pagos/iniciar-prueba', { usuario_id: user.id, email: user.email })
      } catch (err) {
        console.error('Error iniciando prueba gratis:', err)
      }
    }

    // Si no viene a pagar nada nuevo, revisamos si su prueba/plan actual
    // sigue vigente antes de dejarlo entrar.
    await entrarSegunEstadoPlan(user, rol)
  }

  // ── Unica puerta de entrada a la plataforma ('app'): revisa si el plan
  //    del estudiante sigue vigente antes de dejarlo pasar. Esto SIEMPRE
  //    hay que llamarlo antes de mostrar 'app' — nunca hacer
  //    setPantalla('app') directamente desde otro lado, porque si no se
  //    salta esta revision (por ejemplo, si alguien cancela el pago desde
  //    Checkout sin haber pagado nunca, terminaria entrando igual). Solo
  //    aplica a estudiantes — profesores y admin no dependen de un plan
  //    pago. ─────────────────────────────────────────────────────────────
  async function entrarSegunEstadoPlan(user, rol) {
    if (rol === 'estudiante' && user?.id) {
      try {
        const res = await axios.get(`${API}/pagos/estado/${user.id}`)
        const datos = res.data?.datos
        // Las cuentas de administrador (es_admin=true en la base de datos)
        // nunca se bloquean por el estado del plan, sin importar que rol
        // se haya elegido al entrar. Asi el mismo correo que se usa para
        // administrar la plataforma tambien puede entrar como "Estudiante"
        // a probar, aunque su suscripcion este vencida o sin plan.
        if (!datos?.es_admin) {
          // IMPORTANTE: lista de QUE SI se permite (activa/prueba), no de
          // que se bloquea. Una cuenta recien creada (que nunca ha
          // completado un pago en Checkout) no tiene suscripcion_estado en
          // 'sin_plan' -- la fila en "usuarios" se crea sin tocar ese
          // campo (ver progreso.js /inicializar-usuario), asi que queda en
          // null. null no es igual a 'vencida' ni a 'sin_plan', asi que se
          // colaba igual. Con la lista de permitidos, cualquier valor que
          // no sea explicitamente 'activa' o 'prueba' (null incluido) se
          // bloquea por defecto.
          const estado = datos?.suscripcion_estado
          const permitido = estado === 'activa' || estado === 'prueba'
          if (!permitido) {
            // Antes esto mandaba a la pantalla intermedia "Tu prueba o tu
            // plan ya vencieron" (PlanVencido.jsx) con un boton para ver los
            // planes. Igual que en EspañolGo, ahora va directo a precios --
            // un clic menos para elegir/renovar el plan.
            setPantalla('precios')
            return
          }
        }
      } catch (err) {
        console.error('Error revisando el estado del plan:', err)
        // Si la consulta falla por un error tecnico, no lo dejamos
        // trabado aqui — lo dejamos pasar y seguimos con el flujo normal.
      }
    }

    setPantalla('app')
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    setUsuario(null)
    setRolUsuario(null)
    setPantalla('landing')
  }

  function handleElegirPlan(plan) {
    if (plan.esBack) { setPantalla('landing'); return }
    setPlanElegido(plan)
    setPantalla('login')
  }

  function handleTerminarExamen(resultado, datosParaRevision) {
    setNivelExamen(resultado)
    setDatosRevision(datosParaRevision)
    setPantalla('precios')
  }

  if (pantalla === 'email-confirmado') return (
    <EmailConfirmado onContinuar={handleContinuarConfirmado} />
  )

  if (pantalla === 'landing') return (
    <Landing
      onEmpezarExamen={() => setPantalla('examen')}
      onVerPlanes={() => setPantalla('precios')}
      onLogin={() => { setEntrarSoloEstudiante(false); setPantalla('login') }}
      onCrearCuenta={handleCrearCuenta}
    />
  )

  if (pantalla === 'examen') return (
    <ExamenNivel
      onTerminar={handleTerminarExamen}
      onSaltar={() => setPantalla('precios')}
      onVolver={() => setPantalla('landing')}
    />
  )

  if (pantalla === 'precios') return (
    <Precios onContinuar={handleElegirPlan} />
  )

  if (pantalla === 'login') return (
    <Login
      onLogin={handleLogin}
      planElegido={planElegido}
      datosRevision={datosRevision}
      soloEstudiante={entrarSoloEstudiante}
      onVolver={() => setPantalla(planElegido ? 'precios' : 'landing')}
    />
  )

  if (pantalla === 'checkout') return (
    <Checkout
      usuario={usuario}
      plan={planElegido}
      onListo={() => { setPlanElegido(null); entrarSegunEstadoPlan(usuario, rolUsuario) }}
      onVolver={() => { setPlanElegido(null); setPantalla('precios') }}
    />
  )

  if (pantalla === 'checkout-confirmando') return (
    <Checkout
      usuario={usuario}
      modoConfirmacion
      idTransaccion={idTransaccionRedirigida}
      onListo={() => { setPlanElegido(null); entrarSegunEstadoPlan(usuario, rolUsuario) }}
    />
  )

  if (pantalla === 'plan-vencido') return (
    <PlanVencido
      onElegirPlan={() => setPantalla('precios')}
      onCerrarSesion={handleLogout}
    />
  )

  if (pantalla === 'app' && usuario && rolUsuario === 'admin') return (
    <PanelAdmin usuario={usuario} onLogout={handleLogout} />
  )

  if (pantalla === 'app' && usuario && rolUsuario === 'profesor') return (
    <PanelProfesor usuario={usuario} onLogout={handleLogout} />
  )

  if (pantalla === 'app' && usuario) return (
    <Dashboard usuario={usuario} onLogout={handleLogout} />
  )

  return null
}
